import {
  ExploitExplore,
  ExploitExploreType,
  EditableAllocationRow,
  PostOptimizationEditState,
  ExploitGroupRow,
  ExploreRow,
  FlattenedGroupRow,
  NonExtrapolationRow,
  OverallBudgetRow,
  OverallBudgetRowRaw,
  PlacementAnalyticsRow,
  SavedPostOptimizationPlacementEdit,
  PostOptimizationResponse,
  PostOptimizationSavePayload,
  TopLevelRow,
  isExploreRow,
  isFlattenedGroup
} from './post-optimization-constants';

/**
 * Normalize overallBudget from raw API format to frontend format
 */
const normalizeOverallBudget = (raw: OverallBudgetRowRaw[]): OverallBudgetRow[] =>
  raw.map(row => ({
    placement_id: row['Placement ID'],
    placement_code: row['Placement Code'],
    cost: row.Cost
  }));

/**
 * Build a Set of placement codes that have been "seen" (have historical data)
 * This is used for O(1) Exploit/Explore lookup
 */
const buildSeenPlacementSet = (seenPlacements: NonExtrapolationRow[]): Set<number> =>
  new Set(seenPlacements.map(row => row.placement_code));

/**
 * Determine if a placement is Exploit (seen before) or Explore (new)
 * Note: Currently unused but kept for future use when seenPlacements is fixed
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getExploitExplore = (
  placementCode: number | undefined,
  seenPlacementCodes: Set<number>
): ExploitExploreType => {
  if (!placementCode) {
    return ExploitExplore.EXPLORE;
  }
  return seenPlacementCodes.has(placementCode) ? ExploitExplore.EXPLOIT : ExploitExplore.EXPLORE;
};

/**
 * Calculate CPM from total spend and impressions.
 *
 * Guards on exactly 0, unlike src/utils/metric-utils.ts's calculateCPM, which guards falsily: here
 * a NaN/undefined/null impression count surfaces as NaN/Infinity rather than a plausible-looking 0.
 */
const calculateCPM = (totalSpend: number, totalImpressions: number): number => {
  if (totalImpressions === 0) {
    return 0;
  }
  return (totalSpend / totalImpressions) * 1000;
};

/**
 * Calculate aggregated CPM for a group of placements
 * Sums all spend and impressions, then calculates CPM
 */
const calculateGroupCPM = (placements: PlacementAnalyticsRow[]): number => {
  const totalSpend = placements.reduce((sum, p) => sum + p.total_spend, 0);
  const totalImpressions = placements.reduce((sum, p) => sum + p.total_impressions, 0);
  return calculateCPM(totalSpend, totalImpressions);
};

/**
 * Create common line item fields shared between flattened groups and regular line items
 */
const createLineItemFields = (placement: PlacementAnalyticsRow, parent: string) => ({
  placement: placement.placement_name || '',
  lastLive: placement.window_end_est,
  parentId: parent,
  apiData: placement
});

/**
 * Transform raw API response into hierarchical table data structure
 *
 * Structure:
 * - Group rows (type: 'group'): Represent parent networks (e.g., "TREMOR_")
 *   - Line item rows (type: 'lineItem'): Individual placements under each parent
 * - Single-placement groups collapse into one row (type: 'flattenedGroup')
 */
export const transformPostOptimizationData = (
  rawResponse: PostOptimizationResponse
): TopLevelRow[] => {
  const {
    overallBudget: overallBudgetRaw,
    placementAnalytics,
    seenPlacements,
    placementMap,
    optimizerCPM = {}
  } = rawResponse;

  const overallBudget = normalizeOverallBudget(overallBudgetRaw);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const seenPlacementCodes = buildSeenPlacementSet(seenPlacements);

  const formatMap = new Map<number, string>();
  placementMap.forEach(row => {
    const code = parseInt(row.placement_code, 10);
    if (!Number.isNaN(code)) {
      formatMap.set(code, row.format);
    }
  });

  const groupedByParent = placementAnalytics.reduce(
    (acc, placement) => {
      const { parent } = placement;
      if (!acc[parent]) {
        acc[parent] = [];
      }
      acc[parent].push(placement);
      return acc;
    },
    {} as Record<string, PlacementAnalyticsRow[]>
  );

  const hierarchicalData: TopLevelRow[] = [];

  overallBudget.forEach(budgetRow => {
    const parent = budgetRow.placement_id;
    const allPlacements = groupedByParent[parent] || [];
    const format = (formatMap.get(budgetRow.placement_code) || '').toUpperCase();
    const isExplore = allPlacements.length === 0;

    // Calculate CPM from all placements (including control)
    const cpm = isExplore
      ? optimizerCPM[budgetRow.placement_code] || 0
      : calculateGroupCPM(allPlacements);

    // Filter out control placements after CPM calculation
    const placements = allPlacements.filter(placement => {
      const placementName = placement.placement_name?.toLowerCase() || '';
      return !placementName.includes('control');
    });

    if (isExplore) {
      const exploreRow: ExploreRow = {
        id: parent,
        type: 'group',
        networkContent: parent,
        budget: budgetRow.cost,
        aggregateCpm: cpm,
        exploitExplore: ExploitExplore.EXPLORE,
        format,
        subRows: []
      };

      hierarchicalData.push(exploreRow);
      return;
    }

    if (placements.length === 1) {
      const placement = placements[0];
      const flattenedRow: FlattenedGroupRow = {
        id: placement.line_item_id,
        type: 'flattenedGroup',
        networkContent: parent,
        budget: budgetRow.cost,
        aggregateCpm: cpm,
        lineItemCpm: calculateCPM(placement.total_spend, placement.total_impressions),
        exploitExplore: ExploitExplore.EXPLOIT,
        format,
        ...createLineItemFields(placement, parent)
      };

      hierarchicalData.push(flattenedRow);
      return;
    }

    // Shared by all line items in the group; values are never edited after the transform
    const parentValues = {
      networkContent: parent,
      budget: budgetRow.cost,
      aggregateCpm: cpm,
      exploitExplore: 'Exploit' as const,
      format
    };

    const groupRow: ExploitGroupRow = {
      id: `group-${parent}`,
      type: 'group',
      ...parentValues,
      subRows: placements.map(placement => ({
        id: placement.line_item_id,
        type: 'lineItem',
        lineItemCpm: calculateCPM(placement.total_spend, placement.total_impressions),
        parentValues,
        ...createLineItemFields(placement, parent)
      }))
    };

    hierarchicalData.push(groupRow);
  });

  return hierarchicalData;
};

/**
 * Map a function over every editable row (Explore rows, flattened groups, and
 * group children), rebuilding the hierarchy without mutating the input.
 */
export const mapEditableAllocationRows = (
  hierarchicalData: TopLevelRow[],
  transform: <TRow extends EditableAllocationRow>(row: TRow) => TRow
): TopLevelRow[] =>
  hierarchicalData.map(row =>
    isFlattenedGroup(row) || isExploreRow(row)
      ? transform(row)
      : { ...row, subRows: row.subRows.map(lineItem => transform(lineItem)) }
  );

/**
 * Flatten hierarchical data into editable allocation rows, excluding Exploit
 * group headers.
 */
export const flattenEditableAllocationRows = (
  hierarchicalData: TopLevelRow[]
): EditableAllocationRow[] =>
  hierarchicalData.flatMap<EditableAllocationRow>(row =>
    isFlattenedGroup(row) || isExploreRow(row) ? row : row.subRows
  );

/**
 * Apply edits to hierarchical data and flag rows with unsaved changes (does not mutate input)
 */
export const applyEditsToData = (
  hierarchicalData: TopLevelRow[],
  edits: Map<string, PostOptimizationEditState>,
  unsavedEditIds?: ReadonlySet<string>
): TopLevelRow[] => {
  if (edits.size === 0 && !unsavedEditIds?.size) {
    return hierarchicalData;
  }

  return mapEditableAllocationRows(hierarchicalData, row => {
    const rowEdits = edits.get(row.id);
    const isEdited = unsavedEditIds?.has(row.id) ?? false;
    if (!rowEdits && !isEdited) {
      return row;
    }
    return { ...row, ...rowEdits, isEdited };
  });
};

/**
 * Prepare the save payload from active post-optimization edits.
 */
export const prepareSavePayload = (
  hierarchicalData: TopLevelRow[],
  allocationCriteria: keyof PlacementAnalyticsRow,
  runId: string,
  company: string,
  streamingType: string
): PostOptimizationSavePayload => {
  const placementEdits: SavedPostOptimizationPlacementEdit[] = flattenEditableAllocationRows(
    hierarchicalData
  )
    .filter(p => p.mustInclude || p.mustExclude)
    .map(p => ({
      placement_id: p.id,
      must_include: p.mustInclude || false,
      must_exclude: p.mustExclude || false,
      should_reallocate_budget: p.shouldReallocateBudget || false
    }));

  return {
    run_id: runId,
    company,
    streaming_type: streamingType,
    allocation_criteria: allocationCriteria,
    timestamp: new Date().toISOString(),
    placement_edits: placementEdits
  };
};
